import { useAuth } from '@cmg/auth';
import { Stack } from '@cmg/design-system';
import defaultTo from 'lodash/defaultTo';
import React from 'react';

import { useCheckFinalInstitutionalAllocationAccess } from '../../../../../../common/util/check-access/useCheckFinalInstitutionalAllocationAccess';
import {
  AllocationAcknowledgement,
  IndicationStatus,
  InstitutionalIndicationOrderType,
} from '../../../../../../graphql';
import { NoSyndicateInvestorFirmRelationshipBanner } from '../../../../SNoRelationshipBanners';
import { isInvestorContactRequired } from '../../../../utils/isInvestorContactRequired';
import { useIndicationActivitySyndicateContext } from '../../context/IndicationActivitySyndicateContext';
import AllocationSection from '../allocation/AllocationSection';
import AllocationDetails from '../allocation/components/allocation-details/AllocationDetails';
import useSyndicateAllocation from '../allocation/hooks/useSyndicateAllocation';
import { useInvestorContactEditingState } from '../investor-contact-information/hooks/useInvestorContactEditingState';
import InvestorContactInformationForm from '../investor-contact-information/InvestorContactInformationForm';
import DuplicateIndications from './components/dupes/DuplicateIndications';
import DuplicateIndicationBanner from './syndicate/duplicate-indication-banner/DuplicateIndicationBanner';
import IndicationSectionCreateView from './syndicate/indication-section-create-view/IndicationSectionCreateView';
import IndicationSectionEditView from './syndicate/indication-section-edit-view/IndicationSectionEditView';
import IndicationSectionReadView from './syndicate/indication-section-read-view/IndicationSectionReadView';

export type Props = {
  editable: boolean;
  isInvestorCovered: boolean;
  setInstitutionalIndicationAcknowledgementsError?: (boolean) => void;
  isProspectusDocumentMissing?: boolean;
  isOrderBookOpen: boolean;
  displayMissingContacts: boolean;
  setDisplayMissingContacts: (val: boolean) => void;
};

/**
 * Container component used for rendering the
 * Manage Indication UI.
 */
const ManageSyndicateIndication: React.FC<Props> = ({
  editable,
  isInvestorCovered,
  isProspectusDocumentMissing,
  isOrderBookOpen,
  setDisplayMissingContacts,
}) => {
  const { isEditingInvestorContacts, setIsEditingInvestorContacts } =
    useInvestorContactEditingState();

  const { offeringId, basicPublishedOffering, finalInstitutionalAllocationSet } =
    useIndicationActivitySyndicateContext();
  const { investorContactMandatory } = useAuth();
  const indication = basicPublishedOffering?.orderBook.institutionalIndication;

  const { canRead: canReadFinalInstitutionalAllocation } =
    useCheckFinalInstitutionalAllocationAccess({
      offeringId,
    });

  const [isEditingIndication, setIsEditingIndication] = React.useState(false);

  const investorContactRequired = isInvestorContactRequired(
    investorContactMandatory,
    basicPublishedOffering?.type
  );
  const isInvestorContactMandatory = investorContactRequired && !isProspectusDocumentMissing;

  const finalAllocation = indication?.finalAllocation;

  const isAllocationReleased =
    !!finalInstitutionalAllocationSet?.isVisibleToNonSyndicate &&
    !!finalInstitutionalAllocationSet.isReleased;
  const displayAllocationActions = !(!isAllocationReleased || !finalAllocation);

  const investorReplyStatus = defaultTo(
    finalAllocation?.investorReply?.status,
    AllocationAcknowledgement.Unacknowledged
  );

  const allocationSectionProps = useSyndicateAllocation({
    offeringId,
    indicationId: indication?.id,
    allocationVersion: finalAllocation?.version,
    investorReplyStatus,
    billingAndDeliveryAgentCmgEntityKey: defaultTo(
      indication?.billingAndDeliveryAgentCmgEntityKey,
      null
    ),
  });

  const isCreatingIndication = !indication;

  return (
    <div aria-label="Manage Indication">
      <Stack gap={1}>
        {!isCreatingIndication && !isInvestorCovered && (
          <NoSyndicateInvestorFirmRelationshipBanner />
        )}

        {indication?.status === IndicationStatus.Duplicate && <DuplicateIndicationBanner />}
      </Stack>

      {isCreatingIndication && (
        <IndicationSectionCreateView investorContactMandatory={!!isInvestorContactMandatory} />
      )}

      {!isCreatingIndication && isEditingIndication && (
        <IndicationSectionEditView onFinishEditing={() => setIsEditingIndication(false)} />
      )}
      {!isCreatingIndication && !isEditingIndication && (
        <IndicationSectionReadView
          onStartEditing={() => setIsEditingIndication(true)}
          isIndicationEditable={editable}
          isInvestorCovered={isInvestorCovered}
          startEditingInvestorContacts={() => setIsEditingInvestorContacts(true)}
        />
      )}

      {indication && (
        <InvestorContactInformationForm
          investorContactMandatory={isInvestorContactMandatory}
          offering={basicPublishedOffering!}
          indication={indication}
          isCoveredAccount={false}
          setDisplayMissingContacts={setDisplayMissingContacts}
          isInvestorCoveredAndEditable={editable}
          isEditingInvestorContacts={isEditingInvestorContacts}
          setIsEditingInvestorContacts={setIsEditingInvestorContacts}
          isInvestorContactRequired={investorContactRequired}
        />
      )}

      {canReadFinalInstitutionalAllocation &&
        indication &&
        indication.type !== InstitutionalIndicationOrderType.Pass && (
          <AllocationSection
            sectionTitle="Final Allocation"
            isReleased={isAllocationReleased}
            displayAllocationActions={displayAllocationActions}
            {...allocationSectionProps}
          >
            <AllocationDetails
              offeringId={basicPublishedOffering?.id!}
              isReleased={isAllocationReleased}
              allocationData={{
                offerPrice: basicPublishedOffering.offerPrice!,
                shareQuantity: defaultTo(finalAllocation?.shareQuantity, null),
                pricingCurrencyCode: basicPublishedOffering.pricingCurrencyCode,
                orderType: indication.type,
                indicationStatus: indication.status,
                isOrderBookOpen,
                investorReplyStatus,
              }}
            />
          </AllocationSection>
        )}

      {indication && indication.duplicateIndicationIds?.length > 0 && (
        <DuplicateIndications
          duplicateIndicationIds={indication.duplicateIndicationIds ?? []}
          offeringId={offeringId}
        />
      )}
    </div>
  );
};

export default ManageSyndicateIndication;
